// 🔒 LOCKED - DO NOT MODIFY WITHOUT EXPLICIT PERMISSION
// This component is working correctly and has been tested
// Last modified: 2025-12-08
// Any changes may break the Scholarship Report functionality

import { filterSessionsByCourse } from '../../utils/sessionUtils';
import React, { useState, useEffect } from 'react';
import { Search, Download, Filter, X, Award, TrendingDown, Users, Percent } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useReportPagination } from '@/components/ReportPagination';

const ScholarshipReport = () => {
  const [scholarshipData, setScholarshipData] = useState({ summary: {}, students: [] });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    studentId: '',
    session: '',
    course: ''
  });

  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [students, setStudents] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchMasterData();
    fetchScholarshipReport();
  }, []);

  const fetchMasterData = async () => {
    try {
      const [coursesRes, sessionsRes, studentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/master-settings/courses`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/master-settings/sessions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/students/dropdown`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCourses(coursesRes.data);
      setSessions(sessionsRes.data);
      setStudents(studentsRes.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchScholarshipReport = async (customFilters = null) => {
    setLoading(true);
    try {
      const activeFilters = customFilters || filters;
      
      const params = new URLSearchParams();
      if (activeFilters.fromDate) params.append('from_date', activeFilters.fromDate);
      if (activeFilters.toDate) params.append('to_date', activeFilters.toDate);
      if (activeFilters.studentId) params.append('student_id', activeFilters.studentId);
      if (activeFilters.session) params.append('session', activeFilters.session);
      if (activeFilters.course) params.append('course', activeFilters.course);

      const response = await axios.get(
        `${API_URL}/api/fees/scholarship-report?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setScholarshipData(response.data);
    } catch (error) {
      console.error('Error fetching scholarship report:', error);
      alert('Failed to fetch scholarship report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchScholarshipReport();
    setShowFilters(false);
  };

  const clearFilters = () => {
    const clearedFilters = {
      fromDate: '',
      toDate: '',
      studentId: '',
      session: '',
      course: ''
    };
    setFilters(clearedFilters);
    fetchScholarshipReport(clearedFilters);
  };

  // Filter students based on search term
  const filteredStudents = scholarshipData.students.filter(student =>
    student.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.admission_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.course_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.scholarship_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const { paginatedData: pageStudents, PaginationControls } = useReportPagination(filteredStudents);

  const summary = scholarshipData.summary || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Scholarship Report</h1>
        <p className="text-sm text-gray-500 mt-1">View scholarship allocation and beneficiary reports</p>
      </div>

      {/* Summary Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Students</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{summary.total_students || 0}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.students_with_scholarship || 0} with scholarships</p>
            </div>
            <Users className="h-12 w-12 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Scholarship Amount</p>
              <p className="text-3xl font-bold text-purple-600 mt-1">₹{(summary.total_scholarship_amount || 0).toLocaleString('en-IN')}</p>
            </div>
            <Award className="h-12 w-12 text-purple-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Course Fees</p>
              <p className="text-3xl font-bold text-gray-700 mt-1">₹{(summary.total_course_fees || 0).toLocaleString('en-IN')}</p>
            </div>
            <TrendingDown className="h-12 w-12 text-gray-500" />
          </div>
        </div>
      </div>

      {/* Additional Summary Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Average Scholarship/Student</p>
              <p className="text-2xl font-bold text-indigo-700 mt-1">₹{(summary.average_scholarship_per_student || 0).toLocaleString('en-IN')}</p>
            </div>
            <Award className="h-10 w-10 text-indigo-500" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Scholarship % of Total Fees</p>
              <p className="text-2xl font-bold text-emerald-700 mt-1">{(summary.scholarship_percentage_of_fees || 0).toFixed(2)}%</p>
            </div>
            <Percent className="h-10 w-10 text-emerald-500" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search by name, admission number, course, or scholarship..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Filters
            </Button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange('toDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Student</label>
                <select
                  value={filters.studentId}
                  onChange={(e) => handleFilterChange('studentId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Students</option>
                  {students.map(student => (
                    <option key={student.id} value={student.id}>
                      {student.full_name} ({student.admission_number})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Session</label>
                <select
                  value={filters.session}
                  onChange={(e) => handleFilterChange('session', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Sessions</option>
                  {filterSessionsByCourse(sessions, courses.find(c => c.name === filters.course)?.id).map(session => (
                    <option key={session.id} value={session.name}>{session.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Course</label>
                <select
                  value={filters.course}
                  onChange={(e) => handleFilterChange('course', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Courses</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.name}>{course.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={applyFilters} className="bg-blue-600 hover:bg-blue-700 text-white">
                Apply Filters
              </Button>
              <Button onClick={clearFilters} variant="outline">
                Clear All
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading scholarship report...</p>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="p-8 text-center">
            <Award className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No scholarship data available</p>
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Admission No.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Course
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Session
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scholarship Name
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Fee
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scholarship Amount
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    % of Fee
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance Fee
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pageStudents.map((student) => (
                  <tr key={student.student_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {student.admission_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {student.student_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {student.course_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {student.session}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {student.scholarship_name}
                      {student.scholarship_criteria !== 'N/A' && (
                        <p className="text-xs text-gray-500 mt-1">{student.scholarship_criteria}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      ₹{student.total_course_fee.toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-purple-600 font-semibold">
                      ₹{student.scholarship_amount.toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-blue-600 font-medium">
                      {student.percentage_scholarship}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      ₹{student.balance_fee.toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls />
          </>
        )}
      </div>

      {/* Footer Info */}
      {!loading && filteredStudents.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <p className="text-sm text-purple-800">
            <strong>Showing {filteredStudents.length} students</strong> with total scholarships of <strong>₹{(summary.total_scholarship_amount || 0).toLocaleString('en-IN')}</strong>
          </p>
        </div>
      )}
    </div>
  );
};

export default ScholarshipReport;